import { useState } from "react";
import { Search, ChevronRight } from "lucide-react";
import { useFetch, PageHeader, Loading, ErrorState, SectionCard, EmptyState, fmtINR, fmtDate, StatusChip, ExportButton } from "@/lib/ui";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";

const PERIODS = [["today", "Today"], ["7d", "7 days"], ["30d", "30 days"], ["all", "All time"], ["custom", "Custom"]];

function PeriodFilter({ f, setF }) {
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="earnings-period-filter">
      {PERIODS.map(([v, l]) => (
        <Button key={v} size="sm" variant={f.period === v ? "default" : "outline"} onClick={() => setF({ ...f, period: v })} data-testid={`earnings-period-${v}`}>{l}</Button>
      ))}
      {f.period === "custom" && <>
        <Input type="date" value={f.from} onChange={(e) => setF({ ...f, from: e.target.value })} className="w-40" data-testid="earnings-from" />
        <span className="text-sm text-muted-foreground">to</span>
        <Input type="date" value={f.to} onChange={(e) => setF({ ...f, to: e.target.value })} className="w-40" data-testid="earnings-to" />
      </>}
    </div>
  );
}

const qs = (f) => (f.period === "custom" ? `period=custom&from=${f.from}&to=${f.to}` : `period=${f.period}`);

function Stat({ label, value, hint, testid }) {
  return (
    <SectionCard className="p-4" data-testid={testid}>
      <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="text-2xl font-semibold mt-1 tabular-nums">{value}</p>
      {hint && <p className="text-xs text-muted-foreground mt-1">{hint}</p>}
    </SectionCard>
  );
}

function Totals({ t }) {
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
      <Stat label="Earned (delivered)" value={fmtINR(t.earned.net_earnings)} hint={`${t.earned.orders} orders · item value ${fmtINR(t.earned.gross_item_value)}`} testid="earnings-total-earned" />
      <Stat label="Commission (delivered)" value={fmtINR(t.earned.commission)} testid="earnings-total-commission" />
      <Stat label="Upcoming (open orders)" value={fmtINR(t.upcoming.net_earnings)} hint={`${t.upcoming.orders} orders`} testid="earnings-total-upcoming" />
      <Stat label="Upcoming commission" value={fmtINR(t.upcoming.commission)} testid="earnings-total-upcoming-commission" />
    </div>
  );
}

function ShopDetail({ shopId, f, onClose }) {
  const { data, loading, error } = useFetch(shopId ? `/admin/earnings/${shopId}?${qs(f)}` : null, [shopId, f.period, f.from, f.to]);
  return (
    <Sheet open={!!shopId} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-xl overflow-y-auto" data-testid="earnings-shop-sheet">
        <SheetHeader><SheetTitle>{data?.shop_name || "Shop earnings"}</SheetTitle></SheetHeader>
        {loading ? <Loading /> : error ? <ErrorState error={error} /> : data && <div className="mt-4 space-y-4">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <Stat label="Earned" value={fmtINR(data.earned.net_earnings)} hint={`${data.earned.orders} delivered`} testid="sheet-earned" />
            <Stat label="Upcoming" value={fmtINR(data.upcoming.net_earnings)} hint={`${data.upcoming.orders} open`} testid="sheet-upcoming" />
          </div>
          <p className="text-xs text-muted-foreground" data-testid="sheet-notes">Excluded: {data.excluded.cancelled} cancelled, {data.excluded.refunded} refunded · {data.orders_without_commission_record} older orders have no commission record · Shop delivery charges (delivered, not in net): {fmtINR(data.shop_delivery_charges_delivered)}</p>
          <h4 className="text-sm font-medium">Latest orders</h4>
          {data.recent_orders.length === 0 ? <EmptyState label="No orders in this period" /> : (
            <div className="divide-y border rounded-md" data-testid="sheet-recent-orders">
              {data.recent_orders.map((o) => (
                <div key={o.order_id} className="p-3 text-xs flex flex-wrap items-center gap-2">
                  <span className="font-mono">{o.order_number}</span><StatusChip status={o.status} />
                  <span className="text-muted-foreground">{fmtDate(o.created_at)}</span>
                  <span className="ml-auto tabular-nums">{["CANCELLED", "REFUNDED"].includes(o.status) ? <span className="line-through text-muted-foreground" data-testid={`sheet-excluded-${o.order_id}`}>{o.net_earnings != null ? fmtINR(o.net_earnings) : "—"} · excluded</span> : o.net_earnings != null ? `${fmtINR(o.net_earnings)} net · ${o.commission_percent}% (${o.commission_source})` : "No commission record"}</span>
                </div>))}
            </div>)}
        </div>}
      </SheetContent>
    </Sheet>
  );
}

const COLS = [
  { key: "shop_name", label: "Shop" },
  { key: "d", label: "Delivered orders", get: (r) => r.earned.orders }, { key: "dv", label: "Delivered item value", get: (r) => r.earned.gross_item_value },
  { key: "c", label: "Commission", get: (r) => r.earned.commission }, { key: "e", label: "Earned (net)", get: (r) => r.earned.net_earnings },
  { key: "o", label: "Open orders", get: (r) => r.upcoming.orders }, { key: "u", label: "Upcoming (net)", get: (r) => r.upcoming.net_earnings },
];

function ShopTable({ rows, onOpen }) {
  if (!rows.length) return <EmptyState label="No shops match" />;
  return (
    <SectionCard className="overflow-x-auto" data-testid="earnings-shop-table">
      <table className="w-full text-sm">
        <thead className="text-xs text-muted-foreground border-b"><tr>
          <th className="text-left p-3">Shop</th><th className="text-right p-3">Delivered</th><th className="text-right p-3">Commission</th>
          <th className="text-right p-3">Earned (net)</th><th className="text-right p-3">Upcoming (net)</th><th className="p-3" /></tr></thead>
        <tbody>{rows.map((s) => (
          <tr key={s.shop_id} className="border-b last:border-0 hover:bg-muted/40 cursor-pointer" onClick={() => onOpen(s.shop_id)} data-testid={`earnings-row-${s.shop_id}`}>
            <td className="p-3"><div className="font-medium">{s.shop_name}</div>
              <div className="text-xs text-muted-foreground flex gap-2">{s.shop_status}{s.custom_commission_percent != null && <Badge variant="secondary">Custom {s.custom_commission_percent}%</Badge>}</div></td>
            <td className="p-3 text-right tabular-nums">{s.earned.orders}<div className="text-xs text-muted-foreground">{fmtINR(s.earned.gross_item_value)}</div></td>
            <td className="p-3 text-right tabular-nums">{fmtINR(s.earned.commission)}</td>
            <td className="p-3 text-right tabular-nums font-medium" data-testid={`earnings-net-${s.shop_id}`}>{fmtINR(s.earned.net_earnings)}</td>
            <td className="p-3 text-right tabular-nums">{fmtINR(s.upcoming.net_earnings)}<div className="text-xs text-muted-foreground">{s.upcoming.orders} open</div></td>
            <td className="p-3"><ChevronRight className="h-4 w-4 text-muted-foreground" /></td>
          </tr>))}</tbody>
      </table>
    </SectionCard>
  );
}

export default function EarningsPage() {
  const [f, setF] = useState({ period: "30d", from: "", to: "" });
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(null);
  const ready = f.period !== "custom" || (f.from && f.to);
  const { data, loading, error, refetch } = useFetch(ready ? `/admin/earnings?${qs(f)}` : null, [f.period, f.from, f.to]);
  const rows = (data?.shops || []).filter((s) => (s.shop_name || "").toLowerCase().includes(search.toLowerCase()));
  return (
    <div className="space-y-6" data-testid="earnings-page">
      <PageHeader title="Shop Earnings" subtitle="Earned, upcoming and net amounts per shop, from each order's saved commission"
        actions={data && <ExportButton filename={`shop-earnings-${f.period}.csv`} rows={rows} columns={COLS} testid="earnings-export-btn" />} />
      <div className="flex flex-wrap items-center gap-3">
        <PeriodFilter f={f} setF={setF} />
        <div className="relative ml-auto"><Search className="h-4 w-4 absolute left-2.5 top-2.5 text-muted-foreground" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search shop" className="pl-8 w-56" data-testid="earnings-search" /></div>
      </div>
      <p className="text-xs text-amber-700" data-testid="earnings-payout-note">Payouts are not active yet — these amounts are informational only.</p>
      {!ready ? <EmptyState label="Pick both dates" hint="Choose a start and end date for the custom range" />
        : error ? <ErrorState error={error} onRetry={refetch} /> : (loading || !data) ? <Loading /> : <>
          <Totals t={data.totals} />
          <ShopTable rows={rows} onOpen={setOpen} />
        </>}
      <ShopDetail shopId={open} f={f} onClose={() => setOpen(null)} />
    </div>
  );
}
